import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import "./FileShareDialog.scss";
import {
  DialogTitle,
  DialogActions,
  DialogContent,
  Button,
  Autocomplete,
  TextField,
  Chip,
  DialogContentText,
} from "@mui/material";
import ReactLoading from "react-loading";


import { notify } from "../../utils/services/notification";
import ToggleButton from "../../components/ToggleButton/ToggleButton";
import { getCIDAccessConditions, revokeShareAccess, shareFileToAddress } from "../../utils/services/lighthouse";
import { MdContentCopy } from "react-icons/md";
import { copyToClipboard } from "../../utils/services/other";
import { decryptPageURL } from "../../utils/config/urls";
import { sendFileShareEmail } from "../../utils/services/emailService";

const validateEmail = (email: string) => {
  return String(email)
    .toLowerCase()
    .match(
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
    );
};
const validateAddress = (address: string) => {
  return true;
};




interface FileShareDialogProps {
  shareDialogData: any;
  setShareDialogData: any;
}

const FileShareDialog: React.FC<FileShareDialogProps> = ({
  shareDialogData,
}) => {
  const [mailAddress, setMailAddress] = useState<string[]>([]);
  const [publicAddresses, setPublicAddresses] = useState<string[]>([]);
  const [isEmail, setIsEmail] = useState<boolean>(false);
  const [isError, setError] = useState<boolean>(false);
  const [sharedTo, setSharedTo] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);



const shareWithAddress = async (
  cid: string,
  publicAddresses: string[],
  setPublicAddresses: any
) => {
  try {
    setIsLoading(true);
    if (publicAddresses.length > 0) {
      const response = await shareFileToAddress(cid, publicAddresses);
      if (response) {
        fetchAccessConditions();
        notify(`File Shared Successfully`, "success");
        setPublicAddresses([]);
      }
    }
    setIsLoading(false);
  } catch (err) {
    notify(err + "", "error");
  }
};


  const fetchAccessConditions = async () => {
    try {
      if ( shareDialogData?.cid) {
        const conditions = await getCIDAccessConditions(shareDialogData?.cid);
        setSharedTo(conditions?.sharedTo)
      }
    } catch (error) {
      notify(`Error fetching access conditions: ${error}`, "error");
    }
  };

  const removeSharedUser = async (publicAddress:string)=>{
    setIsLoading(true);
    await revokeShareAccess(publicAddress,shareDialogData?.cid);
    await fetchAccessConditions();
    setIsLoading(false);
  }

  useEffect(() => {
    let componentMounted = true;
    componentMounted && fetchAccessConditions();
    return () => {
      componentMounted = false;
    };
  }, [shareDialogData?.cid]);

  const onChangeEmail = (chips: string[]) => {
    setError(false);
    const validatedChips: string[] = [];
    chips.forEach((element) => {
      if (validateEmail(element)) {
        validatedChips.push(element);
      } else {
        setError(true);
      }
    });
    setMailAddress(validatedChips);
  };

  const onChangePublicAddress = (chips: string[]) => {
    const ifDeleted = chips.every((val) => publicAddresses.includes(val));

    if (ifDeleted && chips.length === publicAddresses.length - 1) {
      setPublicAddresses(chips);
    } else {
      setError(false);
      chips.forEach((element) => {
        if (validateAddress(element)) {
          !publicAddresses.includes(element) &&
            setPublicAddresses((state) => [...state, element]);
        } else {
          setError(true);
        }
      });
    }
  };

  return (
    !isLoading ? 
    <>
      <DialogTitle className="title">
        <span>{"Share File"}</span>
        <span>
          {" "}
          <ToggleButton
            setTrue={setIsEmail}
            isTrue={isEmail}
            trueValue={"Email"}
            falseValue={"Public Address"}
            type=""
          />
        </span>
      </DialogTitle>
      {isEmail ? (
        <DialogContent>
          <p style={{ marginBottom: '0px' }}>Share your file link with anyone using email</p>
          <br />
          <small> <b>Please Note:</b> This is just an email notification service. Recipients will not be able to access the file until it is either made public or explicitly shared with their wallet address.</small>
          {isError && <small>Please enter a valid email</small>}

          <DialogContentText>
            <br />

            <div className="inputBox">
              <Autocomplete
                multiple
                id="tags-filled"
                options={[]}
                onChange={(event: any, newValue: any) => {
                  onChangeEmail(newValue);
                }}
                value={mailAddress}
                freeSolo
                renderTags={(value: readonly string[], getTagProps: any) =>
                  value.map((option: string, index: number) => {
                    const { key, ...tagProps } = getTagProps({ index });
                    return (
                      <Chip
                        variant="outlined"
                        label={option}
                        key={key}
                        {...tagProps}
                      />
                    );
                  })
                }
                renderInput={(params: any) => (
                  <TextField
                    {...params}
                    variant="standard"
                    label=""
                    placeholder="Add emails here"
                  />
                )}
              />
            </div>

          </DialogContentText>
        </DialogContent>
      ) : (
        <DialogContent>
          <p>
            Share your file with anyone using their wallet address / sharable address (coreum)
            - each seprated by comma's
            <br />
            {isError && (
              <small style={{ color: "red" }}>
                *Please enter a valid public address
              </small>
            )}
          </p>
          <small> <b>Please Note:</b> Sharing a file directly using the wallet address bypasses any access conditions or restrictions originally applied to the file. Ensure you trust the recipient before proceeding.</small>

          <DialogContentText>
            <br />
            <div className="inputBox">
              <Autocomplete
                multiple
                id="tags-filled"
                options={[]}
                onChange={(event: any, newValue: any) => {
                  onChangePublicAddress(newValue);
                }}
                value={publicAddresses}
                freeSolo
                renderTags={(value: readonly string[], getTagProps: any) =>
                  value.map((option: string, index: number) => {
                    const { key, ...tagProps } = getTagProps({ index });
                    return (
                      <Chip
                        variant="outlined"
                        label={option}
                        key={key}
                        {...tagProps}
                      />
                    );
                  })
                }
                renderInput={(params: any) => (
                  <TextField
                    {...params}
                    variant="standard"
                    label=""
                    placeholder="Enter Public Address"
                  />
                )}
              />
            </div>

          </DialogContentText>

          {
            sharedTo?.length > 0 && <div className="linkShareContainer">
              <div className="shareTitle">
                <span>Shared To:</span>
              </div>

              <div style={{marginBottom:'1.5rem'}}>
              {  sharedTo.map((value: string, index: number) =>  <Chip label={value} variant="outlined" onDelete={()=>{
                removeSharedUser(value)
              }} /> )}
              </div>
                  
            </div>
          }


          <div className="linkShareContainer">
            <div className="shareTitle">
              <span>Sharable Link</span>
              <MdContentCopy
                className="ptr"
                onClick={() => {
                  copyToClipboard(
                    `${decryptPageURL}/${shareDialogData?.["cid"]}`
                  );
                }}
              />
            </div>
            <p>{`${decryptPageURL}/${shareDialogData?.["cid"]}`}</p>
          </div>
        </DialogContent>
      )}
      <DialogActions>
        <p className="files">
          Sharing this file with{" "}
          {isEmail ? mailAddress.length : publicAddresses.length} Users
        </p>
        <Button
          disabled={
            isEmail ? !(mailAddress.length > 0) : !(publicAddresses.length > 0)
          }
          onClick={() => {
            isEmail
              ? sendFileShareEmail(mailAddress, shareDialogData?.cid, setMailAddress)
              : shareWithAddress(
                shareDialogData?.cid,
                publicAddresses,
                setPublicAddresses
              );
          }}
        >
          Share
        </Button>
      </DialogActions>
    </> :   <div className="loadingContainer">
          <ReactLoading
            type={"bubbles"}
            color={"#4452FE"}
            height={"auto"}
            width={375}
          />
        </div>
  );
};

FileShareDialog.propTypes = {
  shareDialogData: PropTypes.object.isRequired,
};

export default FileShareDialog;
